/**
 * Product Customizer — OTRK Dawn Theme
 * Session 1: Core initialization, text input, preview, line item property injection.
 * Session 2: Design gallery — filtering, search, selection, confirmation bar.
 * Session 3: Layout selector — radio buttons with icons, 32oz variant-aware disable.
 * Session 4: Image upload — drag-and-drop, client-side validation, processing stub.
 * Session 5: Quote form — full-custom product quote request, Step 2 text UX.
 * Session 6: Live preview — CSS overlay with mix-blend-mode: multiply on product image.
 * Session 7: Polish — rotated CW/CCW, parallel-stack, material-aware preview, approval modal.
 * Session 9: Overlay moved to Dawn's main product image via JS injection.
 * Session 10: Sidecar-driven engrave zones, variant-aware sizing, separate design/text zones,
 *             secondary preview fix.
 */
(function () {
  'use strict';

  var container = document.getElementById('product-customizer');
  if (!container) return;

  // ── State ──
  var state = {
    selectedDesign: null,
    textLayout: 'horizontal',
    uploadedImageUrl: null,
    engraveSvgUrl: null,
    uploadedImageBase64: null,
    uploadedFileName: null,
    previewUrl: null,
    font: 'Arial',
    variantImageUrl: null,
    blankImageUrl: null,
    variantBlankId: null
  };

  var personalization = container.dataset.personalization;
  var maxLength = parseInt(container.dataset.maxTextLength, 10) || 80;
  var productFormId = container.dataset.productFormId;
  var productRole = (container.dataset.productRole || '').toLowerCase(); // selects centered preset for non-anchor products
  var currentSize = '24oz'; // updated on variant change by onVariantChange()

  // ── Variant → blank_id map ──
  // Dawn's variant JSON can't expose custom variant metafields, so the Liquid loops the
  // product's variants server-side and emits {variantId: blank_id}. The customizer reads it to
  // resolve the selected variant's calibrated zone sidecar.
  var VARIANT_BLANK_MAP = {};
  (function () {
    var el = container.querySelector('[data-otrk-variant-blanks]') ||
             document.querySelector('[data-otrk-variant-blanks]');
    if (!el) return;
    try { VARIANT_BLANK_MAP = JSON.parse(el.textContent) || {}; } catch (e) { VARIANT_BLANK_MAP = {}; }
  })();

  // ── Variant → bare-blank image URL map ──
  // The preview must render personalization on the BLANK, not the hero image (the hero is a
  // marketing composite that may already show a design, which would double up). Emitted from the
  // variant custom.blank_image metafield the intake wizard stamps.
  var VARIANT_BLANK_IMAGE = {};
  (function () {
    var el = container.querySelector('[data-otrk-variant-blank-images]') ||
             document.querySelector('[data-otrk-variant-blank-images]');
    if (!el) return;
    try { VARIANT_BLANK_IMAGE = JSON.parse(el.textContent) || {}; } catch (e) { VARIANT_BLANK_IMAGE = {}; }
  })();

  // The image the preview overlays sit on: the bare blank when we have it, else the variant/hero.
  function previewBaseUrl() { return state.blankImageUrl || state.variantImageUrl || ''; }

  // ── Zone Configs ──
  // Source of truth: generated by shared-library/capabilities/zone-config-generator from the
  // image-pipeline sidecars, loaded as window.OTRK_ZONE_CONFIGS via assets/zone-configs.js.
  var ZONE_CONFIGS = (typeof window !== 'undefined' && window.OTRK_ZONE_CONFIGS) || {};

  // ── Centered Configs (non-anchor products) ──
  // Products WITHOUT a custom.zone_template metafield (cutting board, coaster, memorial, etc.)
  // are "non-anchor": no calibrated sidecar zones. Instead of mis-using tumbler coordinates,
  // they fall back to a generic centered layout keyed by product_role, so design + text land
  // sensibly on any blank with zero per-product calibration. Anchor products (tumbler, sign,
  // plaque) keep using their precise ZONE_CONFIGS entries.
  var CENTERED_CONFIGS = {
    // Wide blank (cutting board): centered band.
    board: {
      'design-only': { engrave: { top: 20, left: 14, width: 72, height: 50 } },
      'text-only':   { text:    { top: 30, left: 12, width: 76, height: 36 } },
      'with-text':   { engrave: { top: 12, left: 16, width: 68, height: 46 }, text: { top: 60, left: 12, width: 76, height: 24 } }
    },
    // Square blank (coaster): centered square.
    coaster: {
      'design-only': { engrave: { top: 16, left: 16, width: 68, height: 68 } },
      'text-only':   { text:    { top: 28, left: 14, width: 72, height: 44 } },
      'with-text':   { engrave: { top: 12, left: 20, width: 60, height: 52 }, text: { top: 66, left: 12, width: 76, height: 20 } }
    },
    // Photo memorial: image fills most of the blank.
    memorial: {
      'design-only': { engrave: { top: 14, left: 16, width: 68, height: 64 } },
      'text-only':   { text:    { top: 30, left: 12, width: 76, height: 40 } },
      'with-text':   { engrave: { top: 10, left: 18, width: 64, height: 54 }, text: { top: 66, left: 12, width: 76, height: 22 } }
    },
    // Generic fallback for any other non-anchor product.
    'default': {
      'design-only': { engrave: { top: 18, left: 20, width: 60, height: 56 } },
      'text-only':   { text:    { top: 30, left: 15, width: 70, height: 40 } },
      'with-text':   { engrave: { top: 14, left: 22, width: 56, height: 44 }, text: { top: 62, left: 15, width: 70, height: 22 } }
    }
  };

  function getCenteredConfig() {
    return CENTERED_CONFIGS[productRole] || CENTERED_CONFIGS['default'];
  }

  // ── Form Discovery ──
  // Dawn's product-form.js creates a <product-form> custom element wrapping the
  // actual <form data-type="add-to-cart-form">. The form ID comes from product_form_id
  // passed through the Liquid template. We need to find this form to inject line item
  // properties before Dawn's FormData submission.
  function findProductForm() {
    var form = document.getElementById(productFormId);
    if (form) return form;
    return document.querySelector('form[data-type="add-to-cart-form"]');
  }

  // ── Line Item Property Injection ──
  function collectProperties() {
    var props = {};

    // Design selection (template, text-and-image modes — Session 2)
    if (state.selectedDesign) {
      props['Design'] = formatDesignName(state.selectedDesign.slug);
      props['_design_slug'] = state.selectedDesign.slug;
      props['_design_svg_url'] = state.selectedDesign.svg_url;
    }

    // Custom text
    var textInput = document.getElementById('customizer-text');
    if (textInput && textInput.value.trim()) {
      props['Custom Text'] = textInput.value.trim();
    }
    var textLine2 = document.getElementById('customizer-text-line2');
    if (textLine2 && textLine2.value.trim()) {
      props['Custom Text Line 2'] = textLine2.value.trim();
    }

    // Text layout (skip if default)
    if (state.textLayout && state.textLayout !== 'horizontal') {
      props['Text Layout'] = formatLayoutName(state.textLayout);
      props['_text_layout'] = state.textLayout;
    }

    // Engraving font — only meaningful when there's custom text. Real family name flows to the
    // webhook (personalization.font) and the F3 production render (which resolves it in R2).
    var _hasText = (textInput && textInput.value.trim()) || (textLine2 && textLine2.value.trim());
    if (_hasText && state.font) props['Font'] = state.font;

    // Uploaded image
    // NOTE: state.uploadedImageBase64 is for local preview only — never inject it
    // as a line item property (Shopify ~5KB limit). When the image pipeline endpoint
    // is built, it will upload to R2 and return a URL for _uploaded_image_url.
    if (state.uploadedImageUrl) {
      props['_uploaded_image_url'] = state.uploadedImageUrl;
    }
    if (state.engraveSvgUrl) {
      props['_engrave_svg_url'] = state.engraveSvgUrl;
    }
    if (state.uploadedFileName) {
      props['Uploaded Image'] = state.uploadedFileName;
    }
    // Upload caption text
    var captionInput = document.getElementById('upload-caption-text');
    if (captionInput && captionInput.value.trim()) {
      props['Image Text'] = captionInput.value.trim();
    }

    // Preview URL
    if (state.previewUrl) {
      props['_preview_url'] = state.previewUrl;
    }

    return props;
  }

  function formatDesignName(slug) {
    return slug.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function formatLayoutName(layout) {
    // Readable names for line item properties
    if (layout === 'rotated-cw') return 'Rotated CW';
    if (layout === 'rotated-ccw') return 'Rotated CCW';
    if (layout === 'parallel-stack') return 'Side by Side';
    return layout.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  var approvalBypassed = false; // set true after modal approval to allow the real submit

  function hookFormSubmission() {
    var form = findProductForm();
    if (!form) {
      requestAnimationFrame(hookFormSubmission);
      return;
    }

    // Primary: formdata event fires just before Dawn's FormData construction
    form.addEventListener('formdata', function (e) {
      var props = collectProperties();
      var keys = Object.keys(props);
      for (var i = 0; i < keys.length; i++) {
        if (props[keys[i]]) {
          e.formData.append('properties[' + keys[i] + ']', props[keys[i]]);
        }
      }
    });

    // Intercept submit for approval modal
    form.addEventListener('submit', function (e) {
      // Inject hidden inputs
      var old = form.querySelectorAll('.otrk-prop-input');
      for (var i = 0; i < old.length; i++) old[i].remove();

      var props = collectProperties();
      var keys = Object.keys(props);
      for (var j = 0; j < keys.length; j++) {
        if (!props[keys[j]]) continue;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + keys[j] + ']';
        input.value = props[keys[j]];
        input.className = 'otrk-prop-input';
        form.appendChild(input);
      }

      // Show approval modal if needed (and not already approved)
      if (!approvalBypassed && needsApprovalModal()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showApprovalModal();
        return false;
      }
      approvalBypassed = false; // reset for next submission
    }, true); // useCapture to run before Dawn's handler
  }

  // ── Text Input Handling ──
  function initTextInput() {
    var line1 = document.getElementById('customizer-text');
    var line2 = document.getElementById('customizer-text-line2');
    var counter1 = document.getElementById('customizer-char-count-1');
    var counter2 = document.getElementById('customizer-char-count-2');

    if (line1 && counter1) {
      line1.addEventListener('input', function () {
        updateCharCount(line1, counter1, maxLength);
        updatePreview();
        enforceExclusivity();
      });
    }

    if (line2 && counter2) {
      line2.addEventListener('input', function () {
        updateCharCount(line2, counter2, maxLength);
        updatePreview();
        updateParallelStackVisibility(line2.value);
        enforceExclusivity();
      });
    }
  }

  function updateCharCount(input, counter, max) {
    var len = input.value.length;
    counter.textContent = len + ' / ' + max;
    if (len >= max * 0.9) {
      counter.classList.add('otrk-customizer__char-count--warn');
    } else {
      counter.classList.remove('otrk-customizer__char-count--warn');
    }
  }

  // ── Main Image Overlay ──
  // Injects the preview overlay into Dawn's product media gallery on top of the
  // active slide image. The overlay elements use the same IDs as before so all
  // existing preview functions (renderPreviewText, updatePreviewDesign, etc.) work
  // unchanged.
  var overlayInjected = false;

  function initMainImageOverlay() {
    // Find Dawn's active media slide
    var host = findMediaHost();
    if (!host) return;

    injectOverlayInto(host);

    // Watch for Dawn's variant-driven image swaps — the active slide may change
    var gallery = document.querySelector('media-gallery');
    if (gallery) {
      var galleryObserver = new MutationObserver(function () {
        var newHost = findMediaHost();
        if (newHost && !newHost.querySelector('#main-preview-overlay')) {
          injectOverlayInto(newHost);
        }
      });
      galleryObserver.observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    // Re-inject on window resize in case Dawn remounts media elements (e.g. layout switch)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var currentHost = findMediaHost();
        if (currentHost && !currentHost.querySelector('#main-preview-overlay')) {
          injectOverlayInto(currentHost);
        }
      }, 200);
    });
  }

  function findMediaHost() {
    // Target .product-media-container (the element whose padding-top creates the exact
    // aspect-ratio box for the product image) rather than the outer .product__media-item <li>.
    // The <li> is a flex/grid slider item whose height is set by the slider container and can
    // be taller than the image at different viewport widths — making overlay % zones wrong.
    // .product-media-container already has position:relative and matches the image exactly.
    var active = document.querySelector('.product__media-item.is-active .product-media-container');
    if (active) return active;
    return document.querySelector('.product__media-item .product-media-container');
  }

  function injectOverlayInto(host) {
    // Remove any previously injected overlay (variant swap creates new slides).
    // Use the specific ID — NOT querySelector('.otrk-preview__main-overlay') which also
    // matches #secondary-main-overlay and would delete it from the DOM on first run.
    var existing = document.getElementById('main-preview-overlay');
    if (existing) existing.remove();

    // Make the host a positioning context
    host.classList.add('otrk-media-overlay-host');

    // Create the overlay container — same structure as the old Liquid preview
    var overlay = document.createElement('div');
    overlay.id = 'main-preview-overlay';
    overlay.className = 'otrk-preview__main-overlay';
    // Separate design-zone and text-zone positioned by sidecar CSS vars (--design-*, --text-*)
    overlay.innerHTML =
      '<div class="otrk-preview__design-zone">' +
        '<img id="customizer-preview-design" class="otrk-preview__design-img" src="" alt="" hidden>' +
        '<p id="customizer-preview-placeholder" class="otrk-preview__placeholder">Your design will appear here</p>' +
      '</div>' +
      '<div class="otrk-preview__text-zone">' +
        '<div id="customizer-preview-text-wrap" class="otrk-preview__text-wrap" hidden>' +
          '<div id="customizer-preview-line1" class="otrk-preview__text-line"></div>' +
          '<div id="customizer-preview-line2" class="otrk-preview__text-line otrk-preview__text-line--sub"></div>' +
        '</div>' +
      '</div>';

    host.appendChild(overlay);
    overlayInjected = true;

    // Put the design/text overlay on the BARE BLANK, not Dawn's hero image (which may already
    // show a composited design → the customer would see it doubled).
    applyMainBaseImage();

    // Re-apply material class, zone config, and re-render current state
    applyMaterialClass();
    applyZoneConfig(currentSize);
    renderPreviewText();
    updatePreviewDesign();
    scheduleFit();
  }

  // Swap the main gallery image to the bare blank. Dawn images carry a srcset (responsive), which
  // overrides src, so clear it. Best-effort: if we have no blank URL we leave Dawn's image alone.
  function applyMainBaseImage() {
    var base = previewBaseUrl();
    if (!base) return;
    var host = document.querySelector('.otrk-media-overlay-host') || findMediaHost();
    if (!host) return;
    var hostImg = host.querySelector('img');
    if (!hostImg) return;
    hostImg.removeAttribute('srcset');
    hostImg.removeAttribute('data-srcset');
    if (hostImg.src !== base) hostImg.src = base;
  }

  // ── Desktop Sticky + Mobile PiP ──
  var pipEl = null;
  var pipDesignImg = null;
  var pipTextEl = null;
  var pipObserver = null;

  function initStickyAndPip() {
    // Mobile PiP: only on small screens
    if (window.innerWidth >= 750) return;

    var host = findMediaHost();
    if (!host) return;

    createPipElement();
    observeImageVisibility(host);
  }

  function createPipElement() {
    if (pipEl) return;

    pipEl = document.createElement('div');
    pipEl.className = 'otrk-pip-preview';
    pipEl.setAttribute('aria-label', 'Scroll to product preview');

    // Product image thumbnail — prefer the selected variant's blank image
    var imgSrc = state.variantImageUrl || '';
    if (!imgSrc) {
      var productImg = document.querySelector('.product__media-item.is-active img, .product__media-item img');
      imgSrc = productImg ? (productImg.currentSrc || productImg.src) : '';
    }

    pipEl.innerHTML =
      '<img class="otrk-pip-preview__img" src="' + escapeAttr(imgSrc) + '" alt="">' +
      '<div class="otrk-pip-preview__overlay">' +
        '<img id="pip-design-img" class="otrk-preview__design-img" src="" alt="" hidden>' +
      '</div>' +
      '<div id="pip-text-indicator" class="otrk-pip-preview__text-indicator" hidden></div>' +
      '<span class="otrk-pip-preview__label">Preview</span>';

    document.body.appendChild(pipEl);
    pipDesignImg = document.getElementById('pip-design-img');
    pipTextEl = document.getElementById('pip-text-indicator');

    // Tap to scroll back to product image
    pipEl.addEventListener('click', function () {
      var target = document.querySelector('.product__media-wrapper');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function observeImageVisibility(host) {
    if (pipObserver) return;

    pipObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          hidePip();
        } else {
          showPip();
        }
      }
    }, { threshold: 0.1 });

    pipObserver.observe(host);
  }

  function showPip() {
    if (!pipEl) return;
    // Sync design to PiP
    updatePipDesign();
    pipEl.classList.add('otrk-pip-preview--visible');
  }

  function hidePip() {
    if (!pipEl) return;
    pipEl.classList.remove('otrk-pip-preview--visible');
  }

  function updatePipDesign() {
    if (!pipDesignImg) return;

    // Keep the PiP base image in sync with the selected variant blank
    var pipBaseImg = pipEl ? pipEl.querySelector('.otrk-pip-preview__img') : null;
    var pipBase = previewBaseUrl();
    if (pipBaseImg && pipBase) {
      pipBaseImg.src = pipBase;
    }

    // Mirror design image (read from state for consistency with secondary preview)
    var designUrl = null;
    if (state.selectedDesign) {
      designUrl = state.selectedDesign.thumbnail_url || state.selectedDesign.thumbnail || null;
    } else if (state.uploadedImageBase64) {
      designUrl = state.uploadedImageBase64;
    } else if (state.uploadedImageUrl) {
      designUrl = state.uploadedImageUrl;
    }
    if (designUrl) {
      pipDesignImg.src = designUrl;
      pipDesignImg.hidden = false;
    } else {
      pipDesignImg.src = '';
      pipDesignImg.hidden = true;
    }

    // Apply material class to pip overlay
    var pipOverlay = pipEl ? pipEl.querySelector('.otrk-pip-preview__overlay') : null;
    if (pipOverlay) {
      var mat = detectMaterial();
      pipOverlay.classList.remove('otrk-preview--steel', 'otrk-preview--wood', 'otrk-preview--glass');
      if (mat !== 'wood') pipOverlay.classList.add('otrk-preview--' + mat);
    }

    // Sync text indicator — read from inputs directly so PiP doesn't depend on main overlay DOM
    if (pipTextEl) {
      var t1El = document.getElementById('customizer-text');
      var t2El = document.getElementById('customizer-text-line2');
      var capEl = document.getElementById('upload-caption-text');
      var text1 = (t1El ? t1El.value.trim() : '') || (capEl ? capEl.value.trim() : '');
      var text2 = t2El ? t2El.value.trim() : '';
      var combined = text1 + (text2 ? ' · ' + text2 : '');
      if (combined) {
        pipTextEl.textContent = combined;
        pipTextEl.hidden = false;
      } else {
        pipTextEl.hidden = true;
      }
    }
  }

  // ── Preview ──
  function updatePreview() {
    renderPreviewText();
    updatePipDesign();
  }

  function renderPreviewText() {
    var textWrap = document.getElementById('customizer-preview-text-wrap');
    var line1El = document.getElementById('customizer-preview-line1');
    var line2El = document.getElementById('customizer-preview-line2');
    var placeholder = document.getElementById('customizer-preview-placeholder');

    // BUGFIX (secondary sync): main-overlay text rendering only runs when the injected
    // overlay nodes exist. This function used to `return` early when they were absent
    // (main image not yet injected / above the fold), which also skipped the secondary-
    // preview sync at the bottom — so text and layout changes never reached the secondary
    // panel (design changes did, via a separate path). Gate ONLY the main-overlay writes;
    // always run the zone reposition + secondary sync + fit afterward.
    if (textWrap && line1El) {
      var line1Input = document.getElementById('customizer-text');
      var line2Input = document.getElementById('customizer-text-line2');
      var text1 = line1Input ? line1Input.value : '';
      var text2 = line2Input ? line2Input.value : '';

      // Also check upload caption
      var captionInput = document.getElementById('upload-caption-text');
      var captionText = captionInput ? captionInput.value : '';

      var hasText = text1 || text2 || captionText;

      if (hasText) {
        var displayText1 = text1 || captionText;
        var displayText2 = text2 || '';

        applyTextLayout(textWrap, line1El, line2El, displayText1, displayText2, state.textLayout);
        applyPreviewFont();
        textWrap.hidden = false;
        if (placeholder) placeholder.hidden = true;
      } else if (!state.selectedDesign) {
        textWrap.hidden = true;
        if (placeholder) placeholder.hidden = false;
      } else {
        textWrap.hidden = true;
      }
    }

    // Always reposition zones (this also positions the secondary overlay) and sync the
    // secondary preview + re-fit, independent of main-overlay injection state.
    applyZoneConfig(currentSize);
    updateSecondaryPreview();
    scheduleFit();
  }

  function applyTextLayout(wrap, line1El, line2El, text1, text2, layout) {
    // Remove all layout classes
    wrap.className = 'otrk-preview__text-wrap';
    wrap.classList.add('otrk-preview__text-wrap--' + (layout || 'horizontal'));

    if (layout === 'vertical-stack') {
      line1El.innerHTML = textToVerticalChars(text1);
      line2El.innerHTML = text2 ? textToVerticalChars(text2) : '';
    } else if (layout === 'parallel-stack') {
      // Side by side: line1 chars in left column, line2 chars in right column
      line1El.innerHTML = textToVerticalChars(text1);
      line2El.innerHTML = text2 ? textToVerticalChars(text2) : '';
      // Both columns render as flex-column children of the flex-row wrap
    } else if (layout === 'monogram' && text1) {
      line1El.textContent = text1.charAt(0);
      line2El.textContent = text1.slice(1) + (text2 ? ' ' + text2 : '');
    } else {
      // horizontal, rotated-cw, rotated-ccw
      line1El.textContent = text1;
      line2El.textContent = text2;
    }
  }

  function textToVerticalChars(text) {
    if (!text) return '';
    var chars = text.split('');
    var html = '';
    for (var i = 0; i < chars.length; i++) {
      html += '<span>' + escapeHtml(chars[i] === ' ' ? '\u00a0' : chars[i]) + '</span>';
    }
    return html;
  }

  // ── Variant Change Detection ──
  // Dawn's product-info.js publishes PUB_SUB_EVENTS.variantChange after re-rendering
  // the product info section. It also updates a hidden input.product-variant-id.
  // We observe that input for value changes to detect variant switches.
  function observeVariantChanges() {
    var variantInput = document.querySelector('input.product-variant-id');
    if (!variantInput) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'value') {
          onVariantChange(variantInput.value);
        }
      }
    });
    observer.observe(variantInput, { attributes: true });

    // Also listen for radio changes in Dawn's variant picker
    var radios = document.querySelectorAll('.product-form__input input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      radios[i].addEventListener('change', (function (vi) {
        return function () {
          setTimeout(function () { onVariantChange(vi.value); }, 50);
        };
      })(variantInput));
    }
  }

  // Resolve the variant's own image (the blank the customizer overlays onto) from the
  // Dawn [data-selected-variant] payload. Prefers featured_media, then featured_image
  // (object or string). Returns null for variants with no assigned image.
  function extractVariantImage(variant) {
    if (!variant) return null;
    if (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) {
      return variant.featured_media.preview_image.src;
    }
    if (variant.featured_image && variant.featured_image.src) {
      return variant.featured_image.src;
    }
    if (typeof variant.featured_image === 'string') {
      return variant.featured_image;
    }
    return null;
  }

  function onVariantChange(variantId) {
    // Find the product JSON data embedded by Dawn's variant-selects
    var jsonEl = document.querySelector('[data-selected-variant]');
    if (!jsonEl) return;

    try {
      var variant = JSON.parse(jsonEl.textContent);
      if (!variant) return;

      // Key the preview to the selected variant's blank image
      state.variantImageUrl = extractVariantImage(variant);
      // The bare blank the personalization previews on (stamped by the intake wizard).
      state.blankImageUrl = VARIANT_BLANK_IMAGE[variant.id] || null;
      // Resolve the variant's blank_id → calibrated zone sidecar (null falls through to centered)
      state.variantBlankId = VARIANT_BLANK_MAP[variant.id] || null;

      var title = variant.title || '';
      var is32oz = title.indexOf('32oz') !== -1
        || variant.option1 === '32oz'
        || variant.option2 === '32oz';

      currentSize = is32oz ? '32oz' : '24oz';
      updateAvailableLayouts(is32oz);
    } catch (e) {
      // Silent fail — variant detection is non-critical
    }
    applyZoneConfig(currentSize);
    applyMainBaseImage();   // re-assert the blank base after Dawn swaps the variant image
    updateSecondaryPreview();
    updatePipDesign();
    enforceExclusivity();
  }

  function updateAvailableLayouts(is32oz) {
    var optionsEl = document.getElementById('layout-options');
    var noteEl = document.getElementById('layout-size-note');
    if (!optionsEl) return;

    var buttons = optionsEl.querySelectorAll('.otrk-customizer__layout-btn');

    if (is32oz) {
      // Disable all non-horizontal layouts, auto-switch to horizontal
      for (var i = 0; i < buttons.length; i++) {
        var layout = buttons[i].dataset.layout;
        if (layout !== 'horizontal') {
          buttons[i].disabled = true;
          buttons[i].setAttribute('aria-checked', 'false');
        }
      }
      // Force-select horizontal
      selectLayout('horizontal', optionsEl);
      if (noteEl) noteEl.hidden = false;
    } else {
      // Re-enable all layouts
      for (var j = 0; j < buttons.length; j++) {
        buttons[j].disabled = false;
      }
      if (noteEl) noteEl.hidden = true;
    }
  }

  // ── Layout Selector ──
  var LAYOUT_ICONS = {
    'horizontal': '<svg class="otrk-customizer__layout-icon" viewBox="0 0 32 32" fill="none" aria-hidden="true"><line x1="4" y1="10" x2="28" y2="10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/><line x1="4" y1="16" x2="22" y2="16" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/><line x1="4" y1="22" x2="25" y2="22" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>',
    'rotated-cw': '<svg class="otrk-customizer__layout-icon" viewBox="0 0 32 32" fill="none" aria-hidden="true"><g transform="rotate(90 16 16)"><line x1="6" y1="12" x2="26" y2="12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/><line x1="6" y1="18" x2="22" y2="18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></g><text x="28" y="28" text-anchor="middle" fill="currentColor" font-family="Barlow,sans-serif" font-size="8" font-weight="700">\u21BB</text></svg>',
    'rotated-ccw': '<svg class="otrk-customizer__layout-icon" viewBox="0 0 32 32" fill="none" aria-hidden="true"><g transform="rotate(-90 16 16)"><line x1="6" y1="12" x2="26" y2="12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/><line x1="6" y1="18" x2="22" y2="18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></g><text x="28" y="28" text-anchor="middle" fill="currentColor" font-family="Barlow,sans-serif" font-size="8" font-weight="700">\u21BA</text></svg>',
    'vertical-stack': '<svg class="otrk-customizer__layout-icon" viewBox="0 0 32 32" fill="none" aria-hidden="true"><text x="16" y="11" text-anchor="middle" fill="currentColor" font-family="Barlow,sans-serif" font-size="7" font-weight="700">H</text><text x="16" y="19" text-anchor="middle" fill="currentColor" font-family="Barlow,sans-serif" font-size="7" font-weight="700">U</text><text x="16" y="27" text-anchor="middle" fill="currentColor" font-family="Barlow,sans-serif" font-size="7" font-weight="700">D</text></svg>',
    'parallel-stack': '<svg class="otrk-customizer__layout-icon" viewBox="0 0 32 32" fill="none" aria-hidden="true"><circle cx="10" cy="8" r="2" fill="currentColor"/><circle cx="10" cy="14" r="2" fill="currentColor"/><circle cx="10" cy="20" r="2" fill="currentColor"/><circle cx="10" cy="26" r="2" fill="currentColor"/><circle cx="22" cy="8" r="2" fill="currentColor"/><circle cx="22" cy="14" r="2" fill="currentColor"/><circle cx="22" cy="20" r="2" fill="currentColor"/></svg>',
    'monogram': '<svg class="otrk-customizer__layout-icon" viewBox="0 0 32 32" fill="none" aria-hidden="true"><text x="16" y="18" text-anchor="middle" fill="currentColor" font-family="Barlow Condensed,sans-serif" font-size="16" font-weight="700">A</text><text x="16" y="28" text-anchor="middle" fill="currentColor" font-family="Barlow,sans-serif" font-size="6" font-weight="600">bc</text></svg>'
  };

  var LAYOUT_LABELS = {
    'horizontal': 'Horizontal',
    'rotated-cw': 'Rotated \u21BB',
    'rotated-ccw': 'Rotated \u21BA',
    'vertical-stack': 'Vertical Stack',
    'parallel-stack': 'Side by Side',
    'monogram': 'Monogram'
  };

  var availableLayouts = [];

  // ── Font selector ──
  // Curated engraving fonts (from the .lbrn2 usage analysis). data-font/family = the REAL family,
  // which flows to the cart property "Font" -> webhook personalization.font -> F3 render.
  var OTRK_FONTS = [
    { family: 'Arial', label: 'Arial' },
    { family: 'Arial Rounded MT Bold', label: 'Rounded' },
    { family: 'Impact', label: 'Impact' },
    { family: 'Bernard MT Condensed', label: 'Bernard' },
    { family: 'Monotype Corsiva', label: 'Corsiva' },
    { family: 'Brush Script MT', label: 'Brush' },
    { family: 'Comic Sans MS', label: 'Comic' }
  ];

  // Apply the chosen font to every preview text surface (main overlay, secondary panel, PiP).
  function applyPreviewFont() {
    // Set a CSS var on each text wrap; the text lines read it (their own font-family would
    // otherwise win over an inherited one).
    var fam = "'" + (state.font || 'Arial').replace(/'/g, '') + "'";
    ['customizer-preview-text-wrap', 'secondary-preview-text-wrap'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.style.setProperty('--otrk-preview-font', fam);
    });
    if (typeof pipTextEl !== 'undefined' && pipTextEl) pipTextEl.style.setProperty('--otrk-preview-font', fam);
  }

  function initFontSelector() {
    var host = document.getElementById('font-options');
    if (!host) return;
    host.innerHTML = OTRK_FONTS.map(function (f) {
      var checked = f.family === state.font ? 'true' : 'false';
      return '<button type="button" class="otrk-font-btn" role="radio" aria-checked="' + checked +
        '" data-font="' + f.family.replace(/"/g, '&quot;') +
        '" style="font-family:\'' + f.family + "',Georgia,serif\">" + f.label + '</button>';
    }).join('');
    host.addEventListener('click', function (e) {
      var btn = e.target.closest('.otrk-font-btn');
      if (!btn) return;
      state.font = btn.dataset.font || 'Arial';
      host.querySelectorAll('.otrk-font-btn').forEach(function (b) {
        b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
      });
      applyPreviewFont();
      scheduleFit();
    });
    applyPreviewFont();
  }

  function initLayoutSelector() {
    var layoutsEl = document.querySelector('.otrk-customizer__layouts');
    if (!layoutsEl) return;

    var jsonRaw = layoutsEl.dataset.layoutsJson;
    if (!jsonRaw) return;

    try {
      availableLayouts = JSON.parse(jsonRaw);
    } catch (e) {
      return;
    }
    if (!Array.isArray(availableLayouts) || availableLayouts.length === 0) return;

    var optionsEl = layoutsEl.querySelector('#layout-options');
    if (!optionsEl) return;

    renderLayoutButtons(optionsEl);
    bindLayoutSelection(optionsEl);
  }

  function renderLayoutButtons(optionsEl) {
    var fragment = document.createDocumentFragment();

    // Expand "rotated" into CW and CCW; add parallel-stack after vertical-stack
    var expanded = [];
    for (var i = 0; i < availableLayouts.length; i++) {
      if (availableLayouts[i] === 'horizontal') continue; // add horizontal last
      if (availableLayouts[i] === 'rotated') {
        expanded.push('rotated-cw');
        expanded.push('rotated-ccw');
      } else {
        expanded.push(availableLayouts[i]);
      }
    }
    // Always add parallel-stack if vertical-stack is present (shown/hidden by JS based on Line 2)
    if (expanded.indexOf('vertical-stack') !== -1 && expanded.indexOf('parallel-stack') === -1) {
      expanded.splice(expanded.indexOf('vertical-stack') + 1, 0, 'parallel-stack');
    }
    // Horizontal always last (full-width bottom button, default selection)
    if (availableLayouts.indexOf('horizontal') !== -1) {
      expanded.push('horizontal');
    }

    for (var j = 0; j < expanded.length; j++) {
      var layout = expanded[j];
      var icon = LAYOUT_ICONS[layout] || '';
      var label = LAYOUT_LABELS[layout] || formatLayoutName(layout);
      var isDefault = layout === 'horizontal';

      var btn = document.createElement('button');
      btn.className = 'otrk-customizer__layout-btn' + (layout === 'horizontal' ? ' otrk-customizer__layout-btn--default' : '');
      btn.type = 'button';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', isDefault ? 'true' : 'false');
      btn.setAttribute('aria-label', label + ' text layout');
      btn.dataset.layout = layout;
      btn.innerHTML = icon + '<span class="otrk-customizer__layout-name">' + escapeHtml(label) + '</span>';

      // parallel-stack hidden by default (shown when Line 2 has content)
      if (layout === 'parallel-stack') {
        btn.hidden = true;
      }

      fragment.appendChild(btn);
    }

    optionsEl.appendChild(fragment);
  }

  function bindLayoutSelection(optionsEl) {
    optionsEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.otrk-customizer__layout-btn');
      if (!btn || btn.disabled) return;

      selectLayout(btn.dataset.layout, optionsEl);
    });
  }

  function selectLayout(layout, optionsEl) {
    optionsEl = optionsEl || document.getElementById('layout-options');
    if (!optionsEl) return;

    var buttons = optionsEl.querySelectorAll('.otrk-customizer__layout-btn');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('aria-checked', buttons[i].dataset.layout === layout ? 'true' : 'false');
    }

    state.textLayout = layout;

    // Re-render preview text with new layout, then re-fit (rotation/stack change geometry)
    renderPreviewText();
    scheduleFit();
  }

  // ── Design Gallery ──
  var galleryDesigns = [];
  var galleryActiveCategory = 'all';
  var gallerySearchTerm = '';
  var galleryCatCounts = {};
  // Above this many designs, open on a category picker instead of the full grid (the shared
  // catalog is ~550 designs — too much to land on "All"). Curated galleries stay below it.
  var GALLERY_PICKER_THRESHOLD = 40;

  function initGallery() {
    var galleryEl = document.querySelector('.otrk-customizer__gallery');
    if (!galleryEl) return;

    var jsonRaw = galleryEl.dataset.galleryJson;
    if (!jsonRaw) return;

    try {
      galleryDesigns = JSON.parse(jsonRaw);
    } catch (e) {
      return;
    }
    if (!Array.isArray(galleryDesigns) || galleryDesigns.length === 0) return;

    // FIX 2: Filter designs by product type
    galleryDesigns = filterDesignsByProductType(galleryDesigns);
    if (galleryDesigns.length === 0) return;

    buildCategoryTabs(galleryEl);
    renderGalleryGrid(galleryEl);
    bindGallerySearch(galleryEl);
    bindGallerySelection(galleryEl);
    bindSelectionClear();
    updateGalleryCount(galleryEl, galleryDesigns.length, galleryDesigns.length);

    // Category-first landing: for large multi-category galleries (the shared catalog), open on a
    // grid of category tiles rather than dumping every design. Small/curated galleries skip this
    // and behave as before.
    if (galleryDesigns.length > GALLERY_PICKER_THRESHOLD && Object.keys(galleryCatCounts).length > 1) {
      buildCategoryPicker(galleryEl);
      enterPickerMode(galleryEl);
    }

    // LOCKED vs LIBRARY design mode (see DESIGN-MODE-SPEC). A gallery of one design IS a
    // pre-selected/locked design: auto-select it and hide the browse UI (search/tabs/grid) so it
    // reads as "your <design> — add your text", not a pointless one-item gallery.
    if (galleryDesigns.length <= 1) {
      galleryEl.classList.add('otrk-gallery--locked');
      var onlyItem = galleryEl.querySelector('.otrk-gallery__item');
      if (onlyItem) onlyItem.click();
    }
  }

  function filterDesignsByProductType(designs) {
    // Product-type filtering removed — gallery is manually curated via design-curator tool.
    // The gallery JSON on each product only contains designs appropriate for that product type.
    return designs;
  }

  function buildCategoryTabs(galleryEl) {
    var filtersEl = galleryEl.querySelector('.otrk-gallery__filters');
    if (!filtersEl) return;

    // Count per category
    var catCounts = {};
    for (var i = 0; i < galleryDesigns.length; i++) {
      var cat = galleryDesigns[i].category;
      if (cat) catCounts[cat] = (catCounts[cat] || 0) + 1;
    }
    galleryCatCounts = catCounts;
    var categories = Object.keys(catCounts).sort();

    // Update "All" tab with total count
    var allTab = filtersEl.querySelector('[data-category="all"]');
    if (allTab) allTab.textContent = 'All (' + galleryDesigns.length + ')';

    // Build category tabs with counts
    for (var j = 0; j < categories.length; j++) {
      var c = categories[j];
      var btn = document.createElement('button');
      btn.className = 'otrk-gallery__filter-tab';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', 'false');
      btn.setAttribute('data-category', c);
      btn.type = 'button';
      btn.textContent = formatDesignName(c) + ' (' + catCounts[c] + ')';
      filtersEl.appendChild(btn);
    }

    // Click handler for all tabs (event delegation)
    filtersEl.addEventListener('click', function (e) {
      var tab = e.target.closest('.otrk-gallery__filter-tab');
      if (!tab) return;

      var allTabs = filtersEl.querySelectorAll('.otrk-gallery__filter-tab');
      for (var k = 0; k < allTabs.length; k++) {
        allTabs[k].classList.remove('otrk-gallery__filter-tab--active');
        allTabs[k].setAttribute('aria-selected', 'false');
      }
      tab.classList.add('otrk-gallery__filter-tab--active');
      tab.setAttribute('aria-selected', 'true');

      galleryActiveCategory = tab.dataset.category;
      filterGallery(galleryEl);
    });
  }

  // ── Category picker (category-first landing for the shared catalog) ──
  function firstThumbForCategory(cat) {
    for (var i = 0; i < galleryDesigns.length; i++) {
      if ((galleryDesigns[i].category || '') === cat) {
        return galleryDesigns[i].thumbnail_url || galleryDesigns[i].thumbnail || '';
      }
    }
    return '';
  }

  function buildCategoryPicker(galleryEl) {
    var scroll = galleryEl.querySelector('.otrk-gallery__grid-scroll');
    if (!scroll) return;

    var picker = document.createElement('div');
    picker.className = 'otrk-gallery__picker';
    picker.setAttribute('role', 'list');
    picker.setAttribute('aria-label', 'Browse designs by category');

    var cats = Object.keys(galleryCatCounts).sort();
    var html = '';
    for (var i = 0; i < cats.length; i++) {
      var c = cats[i];
      var thumb = firstThumbForCategory(c);
      html +=
        '<button class="otrk-gallery__cat-tile" type="button" role="listitem" data-category="' + escapeAttr(c) + '">' +
          '<span class="otrk-gallery__cat-thumb">' +
            (thumb ? '<img src="' + escapeAttr(thumb) + '" alt="" loading="lazy" width="72" height="72">' : '') +
          '</span>' +
          '<span class="otrk-gallery__cat-name">' + escapeHtml(formatDesignName(c)) + '</span>' +
          '<span class="otrk-gallery__cat-count">' + galleryCatCounts[c] + ' designs</span>' +
        '</button>';
    }
    picker.innerHTML = html;
    scroll.parentNode.insertBefore(picker, scroll);

    picker.addEventListener('click', function (e) {
      var tile = e.target.closest('.otrk-gallery__cat-tile');
      if (!tile) return;
      chooseCategory(galleryEl, tile.dataset.category);
    });

    // "All categories" back control — sits above the tabs, hidden until a category is open.
    var back = document.createElement('button');
    back.className = 'otrk-gallery__back';
    back.type = 'button';
    back.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
        '<polyline points="10,3 5,8 10,13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg> All categories';
    var searchWrap = galleryEl.querySelector('.otrk-gallery__search-wrap');
    if (searchWrap) searchWrap.parentNode.insertBefore(back, searchWrap);
    back.addEventListener('click', function () { backToPicker(galleryEl); });
  }

  // Visibility is driven entirely by the --picker / --browsing classes on the gallery root, so the
  // CSS wins over the sub-elements' own `display` rules (the `hidden` attribute did not).
  function enterPickerMode(galleryEl) {
    galleryEl.classList.add('otrk-gallery--picker');
    galleryEl.classList.remove('otrk-gallery--browsing');
  }

  function chooseCategory(galleryEl, cat) {
    galleryEl.classList.remove('otrk-gallery--picker');
    galleryEl.classList.add('otrk-gallery--browsing');

    // Activate the matching tab so tab state matches the chosen category.
    var filtersEl = galleryEl.querySelector('.otrk-gallery__filters');
    if (filtersEl) {
      var tabs = filtersEl.querySelectorAll('.otrk-gallery__filter-tab');
      for (var k = 0; k < tabs.length; k++) {
        var on = tabs[k].dataset.category === cat;
        tabs[k].classList.toggle('otrk-gallery__filter-tab--active', on);
        tabs[k].setAttribute('aria-selected', on ? 'true' : 'false');
      }
    }

    galleryActiveCategory = cat;
    gallerySearchTerm = '';
    var search = galleryEl.querySelector('#gallery-search');
    if (search) search.value = '';
    filterGallery(galleryEl);
  }

  function backToPicker(galleryEl) {
    gallerySearchTerm = '';
    var search = galleryEl.querySelector('#gallery-search');
    if (search) search.value = '';
    enterPickerMode(galleryEl);
  }

  function renderGalleryGrid(galleryEl) {
    var grid = galleryEl.querySelector('#gallery-grid');
    if (!grid) return;

    var fragment = document.createDocumentFragment();
    for (var i = 0; i < galleryDesigns.length; i++) {
      var design = galleryDesigns[i];
      var item = document.createElement('button');
      item.className = 'otrk-gallery__item';
      item.type = 'button';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.setAttribute('aria-label', formatDesignName(design.slug));
      item.dataset.slug = design.slug;
      item.dataset.category = design.category || '';

      var itemThumbUrl = design.thumbnail_url || design.thumbnail || '';
      item.innerHTML =
        '<img class="otrk-gallery__item-img" src="' + escapeAttr(itemThumbUrl) +
        '" alt="' + escapeAttr(formatDesignName(design.slug)) +
        '" loading="lazy" width="120" height="120">' +
        '<span class="otrk-gallery__item-name">' + escapeHtml(formatDesignName(design.slug)) + '</span>' +
        '<span class="otrk-gallery__item-check" aria-hidden="true">' +
          '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</span>';

      fragment.appendChild(item);
    }
    grid.appendChild(fragment);
  }

  function bindGallerySearch(galleryEl) {
    var searchInput = galleryEl.querySelector('#gallery-search');
    if (!searchInput) return;

    var debounceTimer;
    searchInput.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        gallerySearchTerm = searchInput.value.trim().toLowerCase();
        filterGallery(galleryEl);
      }, 150);
    });
  }

  function filterGallery(galleryEl) {
    var grid = galleryEl.querySelector('#gallery-grid');
    var emptyEl = galleryEl.querySelector('#gallery-empty');
    if (!grid) return;

    var items = grid.querySelectorAll('.otrk-gallery__item');
    var visibleCount = 0;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var slug = item.dataset.slug || '';
      var cat = item.dataset.category || '';

      var matchesCategory = galleryActiveCategory === 'all' || cat === galleryActiveCategory;
      var matchesSearch = !gallerySearchTerm || slug.indexOf(gallerySearchTerm) !== -1;

      if (matchesCategory && matchesSearch) {
        item.hidden = false;
        visibleCount++;
      } else {
        item.hidden = true;
      }
    }

    if (emptyEl) emptyEl.hidden = visibleCount > 0;
    updateGalleryCount(galleryEl, visibleCount, galleryDesigns.length);
  }

  function updateGalleryCount(galleryEl, showing, total) {
    var countEl = galleryEl.querySelector('#gallery-count');
    if (!countEl) return;
    if (showing === total) {
      countEl.textContent = 'Showing all ' + total + ' designs';
    } else {
      countEl.textContent = 'Showing ' + showing + ' of ' + total + ' designs';
    }
  }

  function bindGallerySelection(galleryEl) {
    var grid = galleryEl.querySelector('#gallery-grid');
    if (!grid) return;

    grid.addEventListener('click', function (e) {
      var item = e.target.closest('.otrk-gallery__item');
      if (!item) return;

      var slug = item.dataset.slug;
      var design = null;
      for (var i = 0; i < galleryDesigns.length; i++) {
        if (galleryDesigns[i].slug === slug) {
          design = galleryDesigns[i];
          break;
        }
      }
      if (!design) return;

      // Toggle: if already selected, deselect
      if (state.selectedDesign && state.selectedDesign.slug === slug) {
        clearDesignSelection(galleryEl);
        return;
      }

      // Deselect previous
      var prev = grid.querySelector('.otrk-gallery__item[aria-selected="true"]');
      if (prev) prev.setAttribute('aria-selected', 'false');

      // Select new
      item.setAttribute('aria-selected', 'true');
      state.selectedDesign = design;

      // Update confirmation bar
      showSelectionBar(design);

      // Update preview
      updatePreviewDesign();
      enforceExclusivity();
    });
  }

  function showSelectionBar(design) {
    var bar = document.getElementById('gallery-selection');
    var thumb = document.getElementById('gallery-selection-thumb');
    var nameEl = document.getElementById('gallery-selection-name');
    if (!bar) return;

    if (thumb) {
      // Support both thumbnail_url and thumbnail key names
      var thumbUrl = design.thumbnail_url || design.thumbnail || '';
      if (thumbUrl) {
        thumb.src = thumbUrl;
        thumb.alt = formatDesignName(design.slug);
        thumb.hidden = false;
      } else {
        thumb.hidden = true;
      }
    }
    if (nameEl) nameEl.textContent = formatDesignName(design.slug);
    bar.hidden = false;
  }

  function clearDesignSelection(galleryEl) {
    galleryEl = galleryEl || document.querySelector('.otrk-customizer__gallery');
    state.selectedDesign = null;

    // Clear grid selection
    if (galleryEl) {
      var selected = galleryEl.querySelector('.otrk-gallery__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');
    }

    // Hide confirmation bar
    var bar = document.getElementById('gallery-selection');
    if (bar) bar.hidden = true;

    // Clear preview design
    updatePreviewDesign();
    enforceExclusivity();
  }

  function bindSelectionClear() {
    var clearBtn = document.getElementById('gallery-selection-clear');
    if (!clearBtn) return;
    clearBtn.addEventListener('click', function () {
      clearDesignSelection();
    });
  }

  function updatePreviewDesign() {
    var designImg = document.getElementById('customizer-preview-design');
    var placeholder = document.getElementById('customizer-preview-placeholder');
    if (!designImg) return;

    if (state.selectedDesign) {
      var thumbUrl = state.selectedDesign.thumbnail_url || state.selectedDesign.thumbnail || '';
      if (thumbUrl) {
        designImg.src = thumbUrl;
        designImg.alt = formatDesignName(state.selectedDesign.slug);
        designImg.hidden = false;
      }
      if (placeholder) placeholder.hidden = true;
    } else {
      designImg.src = '';
      designImg.hidden = true;
      var textWrap = document.getElementById('customizer-preview-text-wrap');
      var hasText = textWrap && !textWrap.hidden;
      if (placeholder) placeholder.hidden = hasText;
    }
    applyZoneConfig(currentSize);
    updatePipDesign();
    updateSecondaryPreview();
  }

  function escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ── Image Upload ──
  var UPLOAD_MAX_SIZE = 10 * 1024 * 1024; // 10MB
  var UPLOAD_MIN_DIM = 500;
  var UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

  function initImageUpload() {
    var dropzone = document.getElementById('upload-dropzone');
    if (!dropzone) return;

    var fileInput = document.getElementById('upload-file-input');

    // Click on dropzone triggers file input
    dropzone.addEventListener('click', function (e) {
      if (e.target === fileInput) return;
      fileInput.click();
    });
    dropzone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        fileInput.click();
      }
    });

    // File input change
    fileInput.addEventListener('change', function () {
      if (fileInput.files && fileInput.files[0]) {
        processUploadedImage(fileInput.files[0]);
      }
    });

    // Drag and drop
    dropzone.addEventListener('dragenter', function (e) {
      e.preventDefault();
      dropzone.classList.add('otrk-upload__dropzone--dragover');
    });
    dropzone.addEventListener('dragover', function (e) {
      e.preventDefault();
      dropzone.classList.add('otrk-upload__dropzone--dragover');
    });
    dropzone.addEventListener('dragleave', function (e) {
      e.preventDefault();
      dropzone.classList.remove('otrk-upload__dropzone--dragover');
    });
    dropzone.addEventListener('drop', function (e) {
      e.preventDefault();
      dropzone.classList.remove('otrk-upload__dropzone--dragover');
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        processUploadedImage(e.dataTransfer.files[0]);
      }
    });

    // Retry button
    var retryBtn = document.getElementById('upload-retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        showUploadState('idle');
        fileInput.value = '';
      });
    }

    // Accept button
    var acceptBtn = document.getElementById('upload-accept-btn');
    if (acceptBtn) {
      acceptBtn.addEventListener('click', function () {
        acceptUpload();
      });
    }

    // Redo button
    var redoBtn = document.getElementById('upload-redo-btn');
    if (redoBtn) {
      redoBtn.addEventListener('click', function () {
        resetUpload();
      });
    }

    // Change button (from accepted state)
    var changeBtn = document.getElementById('upload-change-btn');
    if (changeBtn) {
      changeBtn.addEventListener('click', function () {
        resetUpload();
      });
    }

    // Caption text character counter
    var captionInput = document.getElementById('upload-caption-text');
    var captionCount = document.getElementById('upload-caption-count');
    if (captionInput && captionCount) {
      captionInput.addEventListener('input', function () {
        updateCharCount(captionInput, captionCount, maxLength);
      });
    }
  }

  function processUploadedImage(file) {
    // Gate 1: File type
    if (UPLOAD_TYPES.indexOf(file.type) === -1) {
      showUploadState('error', 'Please upload a PNG, JPG, or WebP image.');
      return;
    }

    // Gate 2: File size
    if (file.size > UPLOAD_MAX_SIZE) {
      var sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      showUploadState('error', 'Image is ' + sizeMB + 'MB. Maximum is 10MB.');
      return;
    }

    // Gate 3: Dimensions (async)
    showUploadState('processing', 'Checking image...');

    createImageBitmap(file).then(function (img) {
      if (img.width < UPLOAD_MIN_DIM || img.height < UPLOAD_MIN_DIM) {
        showUploadState('error',
          'Image is ' + img.width + '\u00d7' + img.height + 'px. Minimum is ' + UPLOAD_MIN_DIM + '\u00d7' + UPLOAD_MIN_DIM + 'px for clean engraving.');
        return;
      }

      // Convert to base64 for local storage
      showUploadState('processing', 'Removing background...');

      fileToBase64(file, function (base64) {
        state.uploadedImageBase64 = base64;
        state.uploadedFileName = file.name;

        // Gate 4: Try processing endpoint
        showUploadState('processing', 'Processing for engraving...');
        attemptServerProcessing(file, base64);
      });
    }).catch(function () {
      showUploadState('error', 'Could not read this image. Please try a different file.');
    });
  }

  function attemptServerProcessing(file, base64) {
    var formData = new FormData();
    formData.append('file', file);
    formData.append('mode', 'engrave-ready');
    formData.append('product_type', container.dataset.productType);

    fetch('/apps/otrk-proxy/api/image-pipeline/engrave-ready', {
      method: 'POST',
      body: formData
    }).then(function (res) {
      if (!res.ok) throw new Error('Status ' + res.status);
      return res.json();
    }).then(function (data) {
      if (data.error) throw new Error(data.error);
      // Processing endpoint succeeded
      state.uploadedImageUrl = data.original_url;
      state.engraveSvgUrl = data.engrave_url;
      showUploadResult(base64, data.engrave_preview_url, false);
    }).catch(function () {
      // Endpoint not available yet — use base64 fallback
      state.uploadedImageUrl = null;
      state.engraveSvgUrl = null;
      showUploadResult(base64, null, true);
    });
  }

  function showUploadResult(originalSrc, engraveSrc, isComingSoon) {
    var dropzone = document.getElementById('upload-dropzone');
    var result = document.getElementById('upload-result');
    var previewImg = document.getElementById('upload-preview-img');
    var messageEl = document.getElementById('upload-result-message');

    if (dropzone) dropzone.hidden = true;
    if (result) result.hidden = false;

    if (previewImg) previewImg.src = originalSrc;

    if (messageEl) {
      if (isComingSoon) {
        messageEl.textContent = 'Image processing is coming soon. Your image has been validated and will be processed when you place your order.';
      } else {
        messageEl.textContent = 'Your image is ready for engraving.';
      }
    }
  }

  function acceptUpload() {
    var result = document.getElementById('upload-result');
    var accepted = document.getElementById('upload-accepted');
    var thumbEl = document.getElementById('upload-accepted-thumb');
    var nameEl = document.getElementById('upload-accepted-name');

    if (result) result.hidden = true;
    if (accepted) accepted.hidden = false;

    if (thumbEl && state.uploadedImageBase64) {
      thumbEl.src = state.uploadedImageBase64;
    }
    if (nameEl) {
      nameEl.textContent = state.uploadedFileName || 'Custom image';
    }

    // Enable Step 2 text input
    enableUploadStep2(true);

    // Update preview — show uploaded image in the design overlay
    var designImg = document.getElementById('customizer-preview-design');
    var placeholder = document.getElementById('customizer-preview-placeholder');
    if (designImg && state.uploadedImageBase64) {
      designImg.src = state.uploadedImageBase64;
      designImg.alt = state.uploadedFileName || 'Uploaded image';
      designImg.hidden = false;
    }
    if (placeholder) placeholder.hidden = true;
  }

  function resetUpload() {
    state.uploadedImageUrl = null;
    state.engraveSvgUrl = null;
    state.uploadedImageBase64 = null;
    state.uploadedFileName = null;

    var dropzone = document.getElementById('upload-dropzone');
    var result = document.getElementById('upload-result');
    var accepted = document.getElementById('upload-accepted');
    var fileInput = document.getElementById('upload-file-input');

    if (dropzone) dropzone.hidden = false;
    if (result) result.hidden = true;
    if (accepted) accepted.hidden = true;
    if (fileInput) fileInput.value = '';

    showUploadState('idle');

    // Disable Step 2 text input
    enableUploadStep2(false);

    // Reset preview — hide design overlay, show placeholder
    var designImg = document.getElementById('customizer-preview-design');
    var placeholder = document.getElementById('customizer-preview-placeholder');
    if (designImg) {
      designImg.src = '';
      designImg.hidden = true;
    }
    if (placeholder) placeholder.hidden = false;
  }

  function showUploadState(stateName, message) {
    var idle = document.getElementById('upload-state-idle');
    var processing = document.getElementById('upload-state-processing');
    var error = document.getElementById('upload-state-error');

    if (idle) idle.hidden = stateName !== 'idle';
    if (processing) processing.hidden = stateName !== 'processing';
    if (error) error.hidden = stateName !== 'error';

    if (stateName === 'processing' && message) {
      var textEl = document.getElementById('upload-processing-text');
      if (textEl) textEl.textContent = message;
    }

    if (stateName === 'error' && message) {
      var errorTextEl = document.getElementById('upload-error-text');
      if (errorTextEl) errorTextEl.textContent = message;
    }
  }

  function fileToBase64(file, callback) {
    var reader = new FileReader();
    reader.onload = function () {
      callback(reader.result);
    };
    reader.onerror = function () {
      callback(null);
    };
    reader.readAsDataURL(file);
  }

  // ── Upload Step 2 Toggle ──
  function enableUploadStep2(enabled) {
    var step2 = document.getElementById('upload-step2');
    var captionInput = document.getElementById('upload-caption-text');
    var hintEl = document.getElementById('upload-step2-hint');
    if (!step2) return;

    if (enabled) {
      step2.classList.remove('otrk-upload__step2--disabled');
      if (captionInput) captionInput.disabled = false;
      if (hintEl) hintEl.textContent = '';
    } else {
      step2.classList.add('otrk-upload__step2--disabled');
      if (captionInput) {
        captionInput.disabled = true;
        captionInput.value = '';
      }
      if (hintEl) hintEl.innerHTML = '&mdash; available after uploading your image';
    }
  }

  // ── Quote Form ──
  function initQuoteForm() {
    var submitBtn = document.getElementById('quote-submit-btn');
    if (!submitBtn) return;

    submitBtn.addEventListener('click', function () {
      submitQuote();
    });

    // File preview on selection
    var fileInput = document.getElementById('quote-file');
    if (fileInput) {
      fileInput.addEventListener('change', function () {
        showQuoteFilePreview(fileInput);
      });
    }
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function showQuoteFilePreview(fileInput) {
    var previewEl = document.getElementById('quote-file-preview');
    if (!previewEl) return;

    var file = fileInput.files && fileInput.files[0];
    if (!file) {
      previewEl.hidden = true;
      previewEl.innerHTML = '';
      return;
    }

    var isImage = file.type.indexOf('image/') === 0;
    var thumbHtml;

    if (isImage) {
      var url = URL.createObjectURL(file);
      thumbHtml = '<img class="otrk-quote__file-thumb" src="' + url + '" alt="" width="60" height="60">';
    } else {
      thumbHtml =
        '<svg class="otrk-quote__file-icon" width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">' +
        '<rect x="6" y="2" width="18" height="26" rx="2" stroke="currentColor" stroke-width="2"/>' +
        '<path d="M24 2v8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
        '<line x1="10" y1="16" x2="22" y2="16" stroke="currentColor" stroke-width="1.5"/>' +
        '<line x1="10" y1="20" x2="18" y2="20" stroke="currentColor" stroke-width="1.5"/>' +
        '</svg>';
    }

    previewEl.innerHTML =
      '<div class="otrk-quote__file-row">' +
        thumbHtml +
        '<div class="otrk-quote__file-info">' +
          '<span class="otrk-quote__file-name">' + escapeHtml(file.name) + '</span>' +
          '<span class="otrk-quote__file-size">' + formatFileSize(file.size) + '</span>' +
        '</div>' +
        '<button type="button" class="otrk-quote__file-remove" aria-label="Remove file">' +
          '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
          '<line x1="3" y1="3" x2="11" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
          '<line x1="11" y1="3" x2="3" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
          '</svg>' +
        '</button>' +
      '</div>';

    previewEl.hidden = false;

    // Bind remove button
    var removeBtn = previewEl.querySelector('.otrk-quote__file-remove');
    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        fileInput.value = '';
        previewEl.hidden = true;
        previewEl.innerHTML = '';
      });
    }
  }

  function submitQuote() {
    var nameVal = (document.getElementById('quote-name') || {}).value || '';
    var emailVal = (document.getElementById('quote-email') || {}).value || '';
    var phoneVal = (document.getElementById('quote-phone') || {}).value || '';
    var quantityVal = (document.getElementById('quote-quantity') || {}).value || '1';
    var dateVal = (document.getElementById('quote-date') || {}).value || '';
    var detailsVal = (document.getElementById('quote-details') || {}).value || '';
    var errorEl = document.getElementById('quote-error');
    var submitBtn = document.getElementById('quote-submit-btn');
    var submitting = document.getElementById('quote-submitting');

    // Validation
    if (!nameVal.trim() || !emailVal.trim() || !detailsVal.trim()) {
      if (errorEl) {
        errorEl.textContent = 'Please fill in your name, email, and project details.';
        errorEl.hidden = false;
      }
      return;
    }
    if (errorEl) errorEl.hidden = true;

    // Show submitting state
    if (submitBtn) submitBtn.hidden = true;
    if (submitting) submitting.hidden = false;

    // Build payload
    var payload = {
      name: nameVal.trim(),
      email: emailVal.trim(),
      phone: phoneVal.trim(),
      quantity: quantityVal,
      need_by_date: dateVal,
      details: detailsVal.trim(),
      product_title: document.title,
      product_url: window.location.href
    };

    // Handle reference file (base64 is acceptable for low-volume quotes)
    var fileInput = document.getElementById('quote-file');
    var file = fileInput && fileInput.files && fileInput.files[0];

    if (file && file.size > 10 * 1024 * 1024) {
      if (errorEl) {
        errorEl.textContent = 'File is too large. Maximum size is 10MB.';
        errorEl.hidden = false;
      }
      if (submitBtn) submitBtn.hidden = false;
      if (submitting) submitting.hidden = true;
      return;
    }

    if (file) {
      fileToBase64(file, function (base64) {
        payload.reference_file = base64;
        payload.reference_filename = file.name;
        sendQuote(payload);
      });
    } else {
      sendQuote(payload);
    }
  }

  function sendQuote(payload) {
    var errorEl = document.getElementById('quote-error');
    var submitBtn = document.getElementById('quote-submit-btn');
    var submitting = document.getElementById('quote-submitting');
    var formWrap = document.getElementById('quote-form-wrap');
    var successEl = document.getElementById('quote-success');

    fetch('https://otrkustomkreations.com/api/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error('Status ' + res.status);
      return res.json();
    }).then(function () {
      // Success
      if (formWrap) formWrap.hidden = true;
      if (successEl) successEl.hidden = false;
    }).catch(function (err) {
      // Endpoint unavailable or error
      if (submitting) submitting.hidden = true;
      if (submitBtn) submitBtn.hidden = false;

      var msg = err.message && err.message.indexOf('404') !== -1
        ? 'Quote request couldn\u2019t be sent automatically. Please email us at '
        : 'Something went wrong. Please email us at ';

      if (errorEl) {
        errorEl.innerHTML = escapeHtml(msg) +
          '<a href="mailto:sue@otrkustomkreations.com?subject=Custom%20Quote%20Request" style="color:var(--colonial-blue);text-decoration:underline">' +
          'sue@otrkustomkreations.com</a> with your project details.';
        errorEl.hidden = false;
      }
    });
  }

  // ── Parallel-Stack Visibility ──
  function updateParallelStackVisibility(line2Value) {
    var btn = document.querySelector('.otrk-customizer__layout-btn[data-layout="parallel-stack"]');
    if (!btn) return;

    var hasLine2 = line2Value && line2Value.trim().length > 0;
    btn.hidden = !hasLine2;

    // If parallel-stack was selected but line2 is now empty, switch to horizontal
    if (!hasLine2 && state.textLayout === 'parallel-stack') {
      selectLayout('horizontal');
    }
  }

  // ── Material Detection ──
  function detectMaterial() {
    var material = (container.dataset.material || '').toLowerCase();
    var productType = (container.dataset.productType || '').toLowerCase();

    // Check material attribute first, then product type as fallback
    if (material.indexOf('powder-coated') !== -1 || material.indexOf('steel') !== -1 ||
        material.indexOf('tumbler') !== -1 || material.indexOf('stainless') !== -1 ||
        productType === 'tumbler') {
      return 'steel';
    }
    if (material.indexOf('glass') !== -1) {
      return 'glass';
    }
    if (material.indexOf('slate') !== -1) {
      return 'slate';
    }
    if (material.indexOf('pine') !== -1 || material.indexOf('walnut') !== -1 ||
        material.indexOf('oak') !== -1 || material.indexOf('maple') !== -1 ||
        material.indexOf('cherry') !== -1 || material.indexOf('bamboo') !== -1 ||
        material.indexOf('wood') !== -1) {
      return 'wood';
    }
    return 'wood';
  }

  function applyMaterialClass() {
    // Material class moves to .otrk-preview__main-overlay in the new zone structure
    var overlay = document.querySelector('.otrk-media-overlay-host .otrk-preview__main-overlay');
    if (!overlay) return;
    var mat = detectMaterial();
    overlay.classList.remove('otrk-preview--steel', 'otrk-preview--wood', 'otrk-preview--glass');
    if (mat !== 'wood') { // wood is the CSS default, no class needed
      overlay.classList.add('otrk-preview--' + mat);
    }
  }

  // ── Zone Config ──
  // Determines the current content mode (which sidecar variant to use) and applies
  // CSS custom properties to position the design and text zones on both overlays.

  function detectMode() {
    var hasDesign = !!(state.selectedDesign || state.uploadedImageBase64);
    var hasText = (function () {
      var t1 = document.getElementById('customizer-text');
      var t2 = document.getElementById('customizer-text-line2');
      var cap = document.getElementById('upload-caption-text');
      return !!(
        (t1 && t1.value.trim()) ||
        (t2 && t2.value.trim()) ||
        (cap && cap.value.trim())
      );
    })();

    if (hasDesign && hasText) return 'with-text';
    if (hasDesign) return 'design-only';
    if (hasText) return 'text-only';
    return 'design-only'; // default: show design-zone with placeholder
  }

  // ── Mode Exclusivity (Design XOR Text) ──
  // Some products have no calibrated "with-text" zone (e.g. slate coasters — design-only and
  // text-only sidecars exist, the combined one isn't authored yet). For those, design and text
  // are mutually exclusive: choosing one locks the other so there's a clean switch instead of a
  // silent with-text fallback. Self-lifting — returns false the moment a with-text zone exists
  // for the resolved blank. Tumblers are excluded (24oz has with-text; 32oz keeps its fallback).
  function exclusiveModes() {
    if (personalization !== 'text-and-image') return false;
    if (detectMaterial() === 'steel') return false;
    var key = resolveZoneKey(currentSize);
    var cfg = (key && ZONE_CONFIGS[key]) || getCenteredConfig();
    return !!(cfg && !cfg['with-text']);
  }

  function setSectionLocked(el, locked, hintText) {
    if (!el) return;
    el.classList.toggle('otrk-customizer__locked', locked);
    if (typeof hintText !== 'string') return; // section with no hint (e.g. layout selector)
    var hint = el.querySelector('.otrk-customizer__lock-hint');
    if (locked) {
      if (!hint) {
        hint = document.createElement('p');
        hint.className = 'otrk-customizer__lock-hint';
        // Keep a fieldset's <legend> as the first child so it still renders as the caption.
        var legend = el.querySelector(':scope > legend');
        if (legend) el.insertBefore(hint, legend.nextSibling);
        else el.insertBefore(hint, el.firstChild);
      }
      hint.textContent = hintText;
      hint.hidden = false;
    } else if (hint) {
      hint.hidden = true;
    }
  }

  // Enforce Design XOR Text when exclusiveModes() is active. Called after any design or text
  // change. The first field touched wins; the other locks until it's cleared.
  function enforceExclusivity() {
    var gallery   = document.querySelector('.otrk-customizer__gallery');
    var textGroup = document.querySelector('.otrk-customizer__text-group');
    var layouts   = document.querySelector('.otrk-customizer__layouts');
    var t1 = document.getElementById('customizer-text');
    var t2 = document.getElementById('customizer-text-line2');

    if (!exclusiveModes()) {
      setSectionLocked(gallery, false, '');
      setSectionLocked(textGroup, false, '');
      setSectionLocked(layouts, false);
      if (t1) t1.disabled = false;
      if (t2) t2.disabled = false;
      return;
    }

    var hasDesign = !!state.selectedDesign;
    var hasText = !!((t1 && t1.value.trim()) || (t2 && t2.value.trim()));

    // Design chosen → lock the text + layout sections.
    setSectionLocked(textGroup, hasDesign, 'Remove the design to add custom text instead.');
    setSectionLocked(layouts, hasDesign);
    if (t1) t1.disabled = hasDesign;
    if (t2) t2.disabled = hasDesign;

    // Text entered → lock the design gallery.
    setSectionLocked(gallery, hasText, 'Clear your text to choose a design instead.');
  }

  // Resolve which zone_template a product uses.
  // - Single-template products (wood, metal, slate, glass): custom.zone_template holds the full
  //   key, e.g. "wood-plaque-10x12".
  // - Size-variant products (tumblers): custom.zone_template holds the family base ("tumbler")
  //   and zone_template_sized="true", so the per-variant size is appended → "tumbler-24oz".
  // - No metafield (non-anchor products): returns null so applyZoneConfig() uses the generic
  //   centered layout instead of mis-applying tumbler coordinates.
  function resolveTemplateKey(size) {
    var base = container.dataset.zoneTemplate;
    if (!base) return null;
    if (container.dataset.zoneTemplateSized === 'true' && size) return base + '-' + size;
    return base;
  }

  // Resolution priority: selected variant's blank_id (calibrated per-blank sidecar) →
  // product-level zone_template (+size for tumblers) → null (centered fallback in applyZoneConfig).
  function resolveZoneKey(size) {
    var blankId = state.variantBlankId;
    if (blankId && ZONE_CONFIGS[blankId]) return blankId;
    return resolveTemplateKey(size);
  }

  function applyZoneConfig(size) {
    size = size || currentSize;
    var mode = detectMode();
    var sizeKey = resolveZoneKey(size);
    // Anchor products use their calibrated ZONE_CONFIGS entry; non-anchor products (no
    // zone_template, so sizeKey is null) fall back to the role-based centered layout.
    var sizeConfig = (sizeKey && ZONE_CONFIGS[sizeKey]) || getCenteredConfig();
    if (!sizeConfig) return;

    var config = sizeConfig[mode];
    // Fallback when sidecar variant doesn't exist (e.g. 32oz with-text)
    if (!config) config = sizeConfig['design-only'];
    if (!config) return;

    // Resolve zone coordinates
    var designZoneConfig = config.engrave || null;
    var textZoneConfig = config.text || null;

    // For with-text fallback: borrow text-only coords for the text zone
    if (mode === 'with-text' && !textZoneConfig) {
      var textOnlyConfig = sizeConfig['text-only'];
      if (textOnlyConfig) textZoneConfig = textOnlyConfig.text;
    }
    // For design-only/text-only when the text zone has no separate config: reuse engrave position
    if (!textZoneConfig && designZoneConfig) textZoneConfig = designZoneConfig;

    function applyToOverlay(mainOverlayEl) {
      if (!mainOverlayEl) return;
      if (designZoneConfig) {
        mainOverlayEl.style.setProperty('--design-top',    designZoneConfig.top    + '%');
        mainOverlayEl.style.setProperty('--design-left',   designZoneConfig.left   + '%');
        mainOverlayEl.style.setProperty('--design-width',  designZoneConfig.width  + '%');
        mainOverlayEl.style.setProperty('--design-height', designZoneConfig.height + '%');
      }
      if (textZoneConfig) {
        mainOverlayEl.style.setProperty('--text-top',    textZoneConfig.top    + '%');
        mainOverlayEl.style.setProperty('--text-left',   textZoneConfig.left   + '%');
        mainOverlayEl.style.setProperty('--text-width',  textZoneConfig.width  + '%');
        mainOverlayEl.style.setProperty('--text-height', textZoneConfig.height + '%');
      }
    }

    applyToOverlay(document.querySelector('.otrk-media-overlay-host .otrk-preview__main-overlay'));
    applyToOverlay(document.getElementById('secondary-main-overlay'));

    // Also update PiP overlay position directly (it lives in body, not inside main overlay)
    if (pipEl && designZoneConfig) {
      var pipOverlayEl = pipEl.querySelector('.otrk-pip-preview__overlay');
      if (pipOverlayEl) {
        pipOverlayEl.style.top    = designZoneConfig.top    + '%';
        pipOverlayEl.style.left   = designZoneConfig.left   + '%';
        pipOverlayEl.style.width  = designZoneConfig.width  + '%';
        pipOverlayEl.style.height = designZoneConfig.height + '%';
      }
    }
    scheduleFit();
  }

  // ── Fit Engine (measurement-based, two-axis, transform-aware) ──
  // Replaces the old height-only autoSizeText(). Uses getBoundingClientRect(), which
  // includes CSS transforms, so it measures the *visual* box for rotated text. A single
  // uniform transform:scale() fits both axes while preserving internal ratios (monogram,
  // sub-line). Font size is zone-relative → consistent across viewports, no clipping.
  function fitTextZone(zoneEl, wrapEl, layout) {
    if (!zoneEl || !wrapEl || wrapEl.hidden) return;

    var zoneRect = zoneEl.getBoundingClientRect();
    if (zoneRect.width < 2 || zoneRect.height < 2) return;

    var rotated = (layout === 'rotated-cw' || layout === 'rotated-ccw');
    var rotateDeg = layout === 'rotated-cw' ? 90 : (layout === 'rotated-ccw' ? -90 : 0);

    // Measure natural (unscaled, unrotated) content box.
    wrapEl.style.transform = 'none';
    var natural = wrapEl.getBoundingClientRect();
    var w0 = natural.width, h0 = natural.height;
    if (w0 < 1 || h0 < 1) return;

    // Footprint after rotation (rotation swaps the axes).
    var fpW = rotated ? h0 : w0;
    var fpH = rotated ? w0 : h0;

    // Usable zone with a small safety margin.
    var availW = zoneRect.width  * 0.92;
    var availH = zoneRect.height * 0.92;

    // Largest uniform scale that fits both axes.
    var scale = Math.min(availW / fpW, availH / fpH);

    // Cap upscale so a short name / single-letter monogram doesn't balloon absurdly.
    // Allow growth up to ~the zone height worth of a single base line (~19px).
    var maxScale = zoneRect.height / 19;
    if (maxScale < 1) maxScale = 1;            // never let the cap force a shrink
    if (scale > maxScale) scale = maxScale;
    if (scale <= 0) scale = 0.1;

    var t = '';
    if (rotateDeg) t += 'rotate(' + rotateDeg + 'deg) ';
    t += 'scale(' + scale.toFixed(4) + ')';
    wrapEl.style.transform = t;
  }

  function fitAllPreviews() {
    var layout = state.textLayout || 'horizontal';
    fitTextZone(
      document.querySelector('.otrk-media-overlay-host .otrk-preview__text-zone'),
      document.getElementById('customizer-preview-text-wrap'),
      layout
    );
    fitTextZone(
      document.querySelector('#secondary-main-overlay .otrk-preview__text-zone'),
      document.getElementById('secondary-preview-text-wrap'),
      layout
    );
  }

  // Debounce fit to a frame *after* class/transform changes settle (double rAF).
  var _fitRaf1 = null, _fitRaf2 = null;
  function scheduleFit() {
    if (_fitRaf1) cancelAnimationFrame(_fitRaf1);
    if (_fitRaf2) cancelAnimationFrame(_fitRaf2);
    _fitRaf1 = requestAnimationFrame(function () {
      _fitRaf2 = requestAnimationFrame(fitAllPreviews);
    });
  }

  // ── Secondary Preview Sync ──
  // Mirrors customizer state to the secondary preview panel.
  // Reads design from state (not the main overlay DOM) so it works even when the main
  // overlay hasn't been injected yet (e.g. on desktop when main image is above the fold).
  // Zone positioning (CSS custom properties) is handled separately by applyZoneConfig().
  function updateSecondaryPreview() {
    var secondaryMainOverlay = document.getElementById('secondary-main-overlay');
    if (!secondaryMainOverlay) return;

    // Apply material class to the secondary overlay container
    var mat = detectMaterial();
    secondaryMainOverlay.classList.remove('otrk-preview--steel', 'otrk-preview--wood', 'otrk-preview--glass');
    if (mat !== 'wood') secondaryMainOverlay.classList.add('otrk-preview--' + mat);

    var secondaryDesign = document.getElementById('secondary-preview-design');
    var secondaryPlaceholder = document.getElementById('secondary-preview-placeholder');

    // Read design URL from state — avoids dependency on main overlay DOM presence
    if (secondaryDesign) {
      var designUrl = null;
      var designAlt = '';
      if (state.selectedDesign) {
        designUrl = state.selectedDesign.thumbnail_url || state.selectedDesign.thumbnail || null;
        designAlt = formatDesignName(state.selectedDesign.slug);
      } else if (state.uploadedImageBase64) {
        designUrl = state.uploadedImageBase64;
        designAlt = state.uploadedFileName || 'Uploaded image';
      } else if (state.uploadedImageUrl) {
        designUrl = state.uploadedImageUrl;
        designAlt = state.uploadedFileName || 'Uploaded image';
      }

      if (designUrl) {
        secondaryDesign.src = designUrl;
        secondaryDesign.alt = designAlt;
        secondaryDesign.hidden = false;
        if (secondaryPlaceholder) secondaryPlaceholder.hidden = true;
      } else {
        secondaryDesign.src = '';
        secondaryDesign.hidden = true;
      }
    }

    // Apply text layout directly from inputs + state — independent of main overlay DOM state.
    // This ensures layout class and content are always in sync regardless of whether
    // renderPreviewText() has run on the main overlay or not.
    var secondaryTextWrap = document.getElementById('secondary-preview-text-wrap');
    var secLine1 = document.getElementById('secondary-preview-line1');
    var secLine2 = document.getElementById('secondary-preview-line2');

    var t1El = document.getElementById('customizer-text');
    var t2El = document.getElementById('customizer-text-line2');
    var capEl = document.getElementById('upload-caption-text');
    var text1 = ((t1El ? t1El.value : '') || (capEl ? capEl.value : '')).trim();
    var text2 = (t2El ? t2El.value : '').trim();

    if (secondaryTextWrap && secLine1) {
      if (text1 || text2) {
        applyTextLayout(secondaryTextWrap, secLine1, secLine2 || secLine1, text1, text2, state.textLayout);
        secondaryTextWrap.hidden = false;
        if (secondaryPlaceholder) secondaryPlaceholder.hidden = true;
      } else {
        secondaryTextWrap.hidden = true;
      }
    }

    // Placeholder: show only when no design and no text are visible
    if (secondaryPlaceholder) {
      var hasDesign = secondaryDesign && !secondaryDesign.hidden;
      var hasText = secondaryTextWrap && !secondaryTextWrap.hidden;
      secondaryPlaceholder.hidden = hasDesign || hasText;
    }

    // Sync product image to the SELECTED VARIANT's image (the blank the customizer overlays
    // onto), not just whatever slide Dawn currently shows. Zones are calibrated against the
    // blank, so keying off the variant image keeps overlay alignment correct. Falls back to
    // the active media image when no variant image is resolved (e.g. single-variant products).
    var secondaryProductImg = document.querySelector('.otrk-secondary-preview__product-img');
    if (secondaryProductImg) {
      var baseSrc = previewBaseUrl();
      if (!baseSrc) {
        var activeImg = document.querySelector('.product__media-item.is-active img, .product__media-item img');
        if (activeImg) baseSrc = activeImg.currentSrc || activeImg.src;
      }
      if (baseSrc) secondaryProductImg.src = baseSrc;
    }
    scheduleFit();
  }

  // ── Approval Modal ──
  var approvalModalEl = null;

  function needsApprovalModal() {
    if (personalization === 'full-custom' || personalization === 'template') return false;
    // Check if customer entered custom content
    var textInput = document.getElementById('customizer-text');
    var hasText = textInput && textInput.value.trim();
    var hasUpload = state.uploadedImageBase64 || state.uploadedImageUrl;
    return hasText || hasUpload;
  }

  function createApprovalModal() {
    if (approvalModalEl) return;
    var div = document.createElement('div');
    div.id = 'otrk-approval-modal';
    div.className = 'otrk-approval';
    div.setAttribute('role', 'dialog');
    div.setAttribute('aria-modal', 'true');
    div.setAttribute('aria-label', 'Confirm your personalization');
    div.hidden = true;

    div.innerHTML =
      '<div class="otrk-approval__backdrop"></div>' +
      '<div class="otrk-approval__dialog">' +
        '<h3 class="otrk-approval__title">Confirm Your Personalization</h3>' +
        '<div id="approval-summary" class="otrk-approval__summary"></div>' +
        '<label class="otrk-approval__checkbox">' +
          '<input type="checkbox" id="approval-checkbox">' +
          '<span class="otrk-approval__checkbox-text">' +
            'I confirm the text, spelling, and design are correct. Custom engraved items are made to order and cannot be returned for personalization errors.' +
          '</span>' +
        '</label>' +
        '<div class="otrk-approval__actions">' +
          '<button type="button" class="otrk-btn-secondary" id="approval-edit-btn">Edit</button>' +
          '<button type="button" class="otrk-btn-primary otrk-approval__approve" id="approval-approve-btn">Approve &amp; Add to Cart</button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(div);
    approvalModalEl = div;

    // Bind events
    div.querySelector('.otrk-approval__backdrop').addEventListener('click', closeApprovalModal);
    document.getElementById('approval-edit-btn').addEventListener('click', closeApprovalModal);
    document.getElementById('approval-checkbox').addEventListener('change', function () {
      var approveBtn = document.getElementById('approval-approve-btn');
      if (this.checked) {
        approveBtn.classList.add('otrk-approval__approve--enabled');
      } else {
        approveBtn.classList.remove('otrk-approval__approve--enabled');
      }
    });
    document.getElementById('approval-approve-btn').addEventListener('click', function () {
      if (!document.getElementById('approval-checkbox').checked) return;
      closeApprovalModal();
      submitFormDirectly();
    });

    // ESC key
    div.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeApprovalModal();
    });
  }

  function showApprovalModal() {
    createApprovalModal();
    var summaryEl = document.getElementById('approval-summary');
    if (summaryEl) {
      var html = '';
      var props = collectProperties();
      var displayKeys = ['Design', 'Custom Text', 'Custom Text Line 2', 'Text Layout', 'Uploaded Image', 'Image Text'];
      for (var i = 0; i < displayKeys.length; i++) {
        var key = displayKeys[i];
        if (props[key]) {
          html += '<div class="otrk-approval__row">' +
            '<span class="otrk-approval__row-label">' + escapeHtml(key) + ':</span>' +
            '<span class="otrk-approval__row-value">' + escapeHtml(props[key]) + '</span>' +
          '</div>';
        }
      }
      summaryEl.innerHTML = html || '<p style="color:#9A9689;font-style:italic">No custom selections</p>';
    }

    // Reset checkbox
    var checkbox = document.getElementById('approval-checkbox');
    if (checkbox) checkbox.checked = false;
    var approveBtn = document.getElementById('approval-approve-btn');
    if (approveBtn) approveBtn.classList.remove('otrk-approval__approve--enabled');

    approvalModalEl.hidden = false;
    // Focus trap — focus the checkbox
    if (checkbox) checkbox.focus();
  }

  function closeApprovalModal() {
    if (approvalModalEl) approvalModalEl.hidden = true;
  }

  var pendingFormSubmit = null;

  function submitFormDirectly() {
    approvalBypassed = true;
    // Click the submit button to re-trigger Dawn's normal flow
    var form = findProductForm();
    if (!form) return;
    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.click();
  }

  // ── Full-Custom Mode ──
  function initFullCustomMode() {
    if (personalization !== 'full-custom') return;

    // Hide Dawn's buy buttons — they're in a sibling block
    var buyBlock = document.querySelector('.product-form__buttons');
    if (buyBlock) buyBlock.style.display = 'none';
    var dynamicBtns = document.querySelectorAll('.shopify-payment-button');
    for (var i = 0; i < dynamicBtns.length; i++) {
      dynamicBtns[i].style.display = 'none';
    }
  }

  // ── Resize Re-fit ──
  // The overlay fix keeps zone *positions* correct on resize (CSS % vars).
  // Font size is zone-relative, so text must be re-fit whenever the rendered image resizes.
  function initResizeRefit() {
    // BUGFIX (zone scaling on resize): a breakpoint-crossing resize makes Dawn replace the
    // media node, which detaches the injected overlay. applyZoneConfig() then can't find
    // .otrk-media-overlay-host, so the main preview's zones/text stay at stale scale until
    // the separate 200ms re-inject races in. Make refit self-healing: if the current active
    // host has no overlay, re-inject it now (injectOverlayInto re-applies zones, re-renders,
    // and re-fits); otherwise just re-apply zones and re-fit text.
    function refit() {
      var host = findMediaHost();
      if (host && !host.querySelector('#main-preview-overlay')) {
        injectOverlayInto(host);
      } else {
        applyZoneConfig(currentSize);
        scheduleFit();
      }
    }
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', refit);
      return;
    }
    var ro = new ResizeObserver(refit);
    var host = findMediaHost();
    if (host) ro.observe(host);
    var secFrame = document.querySelector('.otrk-secondary-preview__frame');
    if (secFrame) ro.observe(secFrame);
    // Re-attach to the active media host after variant-driven slide swaps.
    var gallery = document.querySelector('media-gallery');
    if (gallery) {
      var mo = new MutationObserver(function () {
        var h = findMediaHost();
        if (h) ro.observe(h); // observing an already-observed element is a no-op
      });
      mo.observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }
  }

  // ── Zone Calibrator (dev only) ──
  // Visit the product page with ?otrk-calibrate=1 to dial in zone geometry against the
  // real photo. Adjust the numeric fields; zones update live on both previews and text
  // re-fits. Copy the emitted JSON into ZONE_CONFIGS for the current size + mode.
  function initCalibrator() {
    if (location.search.indexOf('otrk-calibrate=1') === -1) return;

    (function boot() {
      var overlay = document.querySelector('.otrk-media-overlay-host .otrk-preview__main-overlay');
      if (!overlay) return setTimeout(boot, 400);

      var st = document.createElement('style');
      st.textContent =
        '.otrk-cal .otrk-preview__design-zone{outline:2px dashed #39f;background:rgba(51,153,255,.15)}' +
        '.otrk-cal .otrk-preview__text-zone{outline:2px dashed #f63;background:rgba(255,102,51,.15)}';
      document.head.appendChild(st);
      overlay.classList.add('otrk-cal');

      var fields = ['design-top','design-left','design-width','design-height',
                    'text-top','text-left','text-width','text-height'];

      function readVar(f) { return parseFloat(overlay.style.getPropertyValue('--' + f)) || 0; }

      var panel = document.createElement('div');
      panel.style.cssText = 'position:fixed;top:8px;left:8px;z-index:100000;background:#0B1620;' +
        'color:#F2ECE0;padding:12px 14px;border-radius:6px;font:12px/1.6 monospace;width:248px;' +
        'box-shadow:0 6px 20px rgba(0,0,0,.5)';
      panel.innerHTML =
        '<b>Zone Calibrator</b><br><small id="cal-ctx"></small>' +
        '<div id="cal-rows" style="margin:8px 0"></div>' +
        '<button id="cal-copy" style="width:100%;padding:6px;background:#CFA44E;border:0;' +
          'border-radius:4px;color:#0B1620;font-weight:700;cursor:pointer">Copy JSON</button>' +
        '<textarea id="cal-out" readonly style="width:100%;height:96px;margin-top:8px;' +
          'background:#152432;color:#9fd;border:1px solid #2a3b4d;border-radius:3px;font:10px monospace"></textarea>';
      document.body.appendChild(panel);

      var rows = panel.querySelector('#cal-rows');
      rows.innerHTML = fields.map(function (f) {
        return '<label style="display:flex;justify-content:space-between;align-items:center;gap:8px">' +
          '<span>' + f + '</span>' +
          '<input data-f="' + f + '" type="number" step="0.5" value="' + readVar(f) + '" ' +
          'style="width:66px;background:#152432;color:#F2ECE0;border:1px solid #2a3b4d;' +
          'border-radius:3px;padding:2px 4px"></label>';
      }).join('');

      function emit() {
        var o = {};
        fields.forEach(function (f) { o[f] = parseFloat(panel.querySelector('[data-f="' + f + '"]').value) || 0; });
        var json = {
          engrave: { top: o['design-top'], left: o['design-left'], width: o['design-width'], height: o['design-height'] },
          text:    { top: o['text-top'],   left: o['text-left'],   width: o['text-width'],   height: o['text-height'] }
        };
        panel.querySelector('#cal-out').value = JSON.stringify(json, null, 2);
      }

      function apply() {
        var sec = document.getElementById('secondary-main-overlay');
        fields.forEach(function (f) {
          var val = panel.querySelector('[data-f="' + f + '"]').value + '%';
          overlay.style.setProperty('--' + f, val);
          if (sec) sec.style.setProperty('--' + f, val);
        });
        scheduleFit();
        emit();
      }

      panel.addEventListener('input', apply);
      panel.querySelector('#cal-copy').addEventListener('click', function () {
        var ta = panel.querySelector('#cal-out'); ta.select(); document.execCommand('copy');
      });

      // Keep the context label + field values in sync with the active size/mode.
      function refresh() {
        panel.querySelector('#cal-ctx').textContent = currentSize + ' · ' + detectMode();
        fields.forEach(function (f) { panel.querySelector('[data-f="' + f + '"]').value = readVar(f); });
        emit();
      }
      setInterval(refresh, 800);
      emit();
    })();
  }

  // ── Initialize ──
  hookFormSubmission();
  initMainImageOverlay();
  initStickyAndPip();
  initTextInput();
  initLayoutSelector();
  initFontSelector();
  initGallery();
  initImageUpload();
  initQuoteForm();
  // Paint the first preview against the INITIALLY-SELECTED variant. Route this through
  // onVariantChange() rather than a partial re-resolve: it reads [data-selected-variant] and
  // sets state.variantImageUrl, state.variantBlankId, currentSize, and the available layouts,
  // then positions both overlays. Doing only image/blank_id here (the old path) left
  // currentSize at its '24oz' default, so a 32oz-selected product painted its first preview
  // with tumbler-24oz zones on both overlays until the first variant toggle — the size-zone
  // mismatch. One code path now resolves variant -> state at init and on every change.
  applyMaterialClass();
  onVariantChange();
  observeVariantChanges();
  initFullCustomMode();
  initResizeRefit();   // re-fit text whenever the image container resizes
  initCalibrator();    // no-op unless ?otrk-calibrate=1

  // ── Hero Pre-fill ──
  // Reads URL params written by the homepage mini-customizer's "View Product" CTA.
  // Runs last so the gallery and text inputs are fully initialized.
  (function () {
    try {
      var params = new URLSearchParams(window.location.search);
      var heroDesign = params.get('_hero_design');
      var heroLine1  = params.get('_hero_line1');
      var heroLine2  = params.get('_hero_line2');
      if (!heroDesign && !heroLine1 && !heroLine2) return;

      // Pre-select design in gallery by slug
      if (heroDesign) {
        var item = document.querySelector('.otrk-gallery__item[data-slug="' + CSS.escape(heroDesign) + '"]');
        if (item) item.click();
      }

      // Pre-fill text inputs
      var t1 = document.getElementById('customizer-text');
      var t2 = document.getElementById('customizer-text-line2');
      if (heroLine1 && t1) {
        t1.value = heroLine1;
        t1.dispatchEvent(new Event('input', { bubbles: true }));
      }
      if (heroLine2 && t2) {
        t2.value = heroLine2;
        t2.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } catch (e) {}
  })();
})();
